import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

const IDOL_GROUPS = ["AURORA", "NEXUS", "PRISM", "기타"];

interface Props {
  onClose: () => void;
}

export default function EditProfileModal({ onClose }: Props) {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    nickname: "",
    bio: "",
    favoriteGroup: "",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        nickname: user.nickname,
        bio: user.bio,
        favoriteGroup: user.favoriteGroup,
      });
    }
  }, [user]);

  const handleSave = () => {
    if (!form.nickname.trim()) return;
    updateUser({
      nickname: form.nickname.trim(),
      bio: form.bio.trim(),
      favoriteGroup: form.favoriteGroup,
    });
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 900);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0f0f1a] border border-white/15 rounded-2xl p-6 z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white font-bold text-lg">프로필 편집</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-5">
          <div>
            <label className="text-white/50 text-xs font-semibold mb-2 block">닉네임 *</label>
            <input
              type="text"
              value={form.nickname}
              onChange={(e) => setForm((p) => ({ ...p, nickname: e.target.value }))}
              placeholder="닉네임 입력"
              maxLength={20}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
            />
            <p className="text-white/25 text-[10px] mt-1 text-right">{form.nickname.length}/20</p>
          </div>

          <div>
            <label className="text-white/50 text-xs font-semibold mb-2 block">자기소개</label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
              placeholder="나를 소개해보세요"
              maxLength={100}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors resize-none"
            />
            <p className="text-white/25 text-[10px] mt-1 text-right">{form.bio.length}/100</p>
          </div>

          <div>
            <label className="text-white/50 text-xs font-semibold mb-2 block">최애 그룹</label>
            <div className="flex flex-wrap gap-2">
              {IDOL_GROUPS.map((g) => (
                <button
                  key={g}
                  onClick={() => setForm((p) => ({ ...p, favoriteGroup: g }))}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    form.favoriteGroup === g
                      ? "border-[#ff6eb4]/60 bg-[#ff6eb4]/15 text-[#ff6eb4]"
                      : "border-white/10 text-white/50 hover:border-white/20"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-white/50 text-sm font-medium hover:border-white/20 hover:text-white/70 transition-all cursor-pointer whitespace-nowrap"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={!form.nickname.trim() || saved}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}
          >
            {saved ? (
              <>
                <i className="ri-check-double-line" />
                저장됨!
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                저장하기
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
